import { FC } from 'react';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { Table } from '../../../components/table/index';
import { useScreenSize } from '../../../hooks/index';
import { useReverseVesting } from '../hooks/index';
import {
  reverseVestingColumnOrderMap,
  reverseVestingColumnTranslationKey,
} from './reverse-vesting.types';
import { ReverseVestingItem } from './reverse-vesting-item';
import { TerminationEvent } from './termination-event';

export type ReverseVestingProps = {
  queriesEnabled: boolean;
  stakeholderId: string;
};

/**
 * Independent from the standard vesting table above it: a stakeholder holding both a grant and a
 * reverse-vested issuance sees two tables, never one merged view.
 */
export const ReverseVesting: FC<ReverseVestingProps> = ({ queriesEnabled, stakeholderId }) => {
  const { reverseVestingRows } = useReverseVesting({ enabled: queriesEnabled, stakeholderId });
  const { isMobile } = useScreenSize();

  // Conditional section — a stakeholder with no reverse-vested issuance sees nothing at all.
  if (!reverseVestingRows.length) return;

  return (
    <>
      <div className="max flex w-full flex-col gap-3 rounded-lg bg-gray-50 p-1 !pt-3 lg:p-[2px]">
        <span className="pl-4 text-[10px] font-bold uppercase leading-4 tracking-[1px] text-gray-700">
          <AppFormattedMessage id="REVERSE_VESTING" />
        </span>

        {isMobile ? (
          <div className="flex w-full flex-col gap-2 rounded-lg bg-white p-3 shadow-sm">
            {reverseVestingRows.map((data) => (
              <ReverseVestingItem data={data} key={data.id} />
            ))}
          </div>
        ) : (
          <div className="flex gap-2 overflow-hidden rounded-md bg-white px-2 pb-2 pt-1 shadow-sm">
            <div className="flex h-full w-full flex-col gap-1 overflow-x-auto rounded-md border border-gray-100">
              <Table
                columnOrder={reverseVestingColumnOrderMap}
                columnsTitle={reverseVestingColumnTranslationKey}
              >
                {reverseVestingRows.map((data) => (
                  <ReverseVestingItem data={data} key={data.id} mode="TABLE" />
                ))}
              </Table>
            </div>
          </div>
        )}
      </div>

      <TerminationEvent rows={reverseVestingRows} />
    </>
  );
};
